<template>
  <div class=" justify-content-around py-2 gap-2">
    <!-- Top Section: Title, Search, and Filters -->
    <div class="bg-white p-4 rounded-lg shadow mb-4">
      <div class="flex flex-col gap-4">
        <div class="flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-800">
            Warehouse Dashboard
          </h2>
          <div class="flex items-center gap-4">
            <!-- Search bar -->
            <span class="relative">
              <i class="pi pi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <InputText 
                v-model="searchQuery" 
                placeholder="Search projects..." 
                class="pl-9 py-2 w-64"
              />
            </span>
          </div>
        </div>
                
        <!-- Filter Badges -->
        <div
          v-if="activeFilters.length > 0"
          class="flex flex-wrap gap-2"
        >
          <Badge 
            v-for="filter in activeFilters" 
            :key="filter"
            :value="filter" 
            severity="info"
            class="cursor-pointer"
            @click="removeFilter(filter)"
          >
            <template #value>
              <div class="flex items-center gap-1">
                <span>{{ filter }}</span>
                <i class="pi pi-times text-xs" />
              </div>
            </template>
          </Badge>
          <span 
            v-if="activeFilters.length > 0" 
            class="text-blue-500 text-sm cursor-pointer"
            @click="clearAllFilters"
          >
            Clear All
          </span>
        </div>
      </div>
    </div>

    <!-- Tabs Section -->
    <div class="bg-white rounded-lg shadow">
      <Tabs :active-tab="'incoming'">
        <TabList>
          <Tab value="incoming">
            Incoming
            <span class="ml-2 text-xs bg-gray-100 text-gray-700 py-1 px-2 rounded-full">
              {{ filteredIncomingCount }}
            </span>
          </Tab>
          <Tab value="outbound">
            Outbound
            <span class="ml-2 text-xs bg-gray-100 text-gray-700 py-1 px-2 rounded-full">
              {{ filteredOutboundCount }}
            </span>
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="incoming">
            <div class="p-4">
              <TodayComponent :projects="filteredIncomingProjects" />
            </div>
          </TabPanel>
          <TabPanel value="outbound">
            <div class="p-4">
              <Projects :projects-data="filteredOutboundProjectsArray" />
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </div>
</template>

<script setup>
// Components in Tabs
import TodayComponent from '@/components/dashboard/widgets/TodayComponent.vue';
import Projects from '@/components/dashboard/widgets/UpcomingWidget.vue'

import { ref, onMounted, computed } from 'vue';
import { createProjects } from '@/data/faker';
import { isToday, addDays, format } from 'date-fns';

const projects = ref([]);
const confirmedProjects = ref([]); // Holds only confirmed projects
const incomingProjects = ref([]);
const outboundProjects = ref([]);

const incomingCount = ref(0);
const outboundCount = ref(0);

const searchQuery = ref('');
const activeFilters = ref(['Confirmed']);

// Computed values for filtered projects
const filteredIncomingProjects = computed(() => {
    const filtered = incomingProjects.value.filter(project => 
        matchesSearch(project) && matchesFilters(project)
    );
    
    console.log("Filtered incoming projects: ", filtered);
    return filtered;
});

const filteredIncomingCount = computed(() => {
    return filteredIncomingProjects.value.length;
});

// Get filtered outbound projects
const filteredOutboundProjects = computed(() => {
    return outboundProjects.value.filter(project => 
        matchesSearch(project) && matchesFilters(project)
    );
});

// Convert the filtered outbound projects to the format expected by the UpcomingWidget
const filteredOutboundProjectsArray = computed(() => {
    return filteredOutboundProjects.value.map(project => {
        // Format the project data to match the expected format
        let formattedTime = '00:00';
        
        try {
            if (project.start) {
                const projectDate = new Date(project.start);
                // Check if the date is valid before formatting
                if (!isNaN(projectDate.getTime())) {
                    formattedTime = format(projectDate, 'HH:mm');
                }
            }
        } catch (e) {
            console.error('Error formatting time:', e, project.start);
        }

        let startDay = 'Upcoming';
        try {
            if (project.start) {
                const projectDate = new Date(project.start);
                // Check if the date is valid before getting day label
                if (!isNaN(projectDate.getTime())) {
                    startDay = getDayLabel(projectDate);
                }
            }
        } catch (e) {
            console.error('Error getting day label:', e, project.start);
        }
        
        return {
            id: project.id,
            status: project.status,
            time: formattedTime,
            start: startDay,
            projectNumber: project.projectNumber || `PRJ-${project.id}`,
            projectName: project.name || 'Unnamed Project',
            projectManager: project.projectManager || 'Unassigned',
            totalItems: project.totalItems || project.itemsCount || 100,
            totalPrepped: project.status === 'confirmed' ? 80 : 30,
            client: project.client || 'Client Name'
        };
    });
});

// Helper function to get day label (Today, Tomorrow, Next Monday, etc.)
const getDayLabel = (date) => {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    
    if (isToday(date)) return 'Today';
    if (date.getDate() === tomorrow.getDate() && 
        date.getMonth() === tomorrow.getMonth() && 
        date.getFullYear() === tomorrow.getFullYear()) return 'Tomorrow';
    
    // Otherwise, return day of week with "Next" prefix
    return `Next ${format(date, 'EEEE')}`;
};

const filteredOutboundCount = computed(() => {
    return filteredOutboundProjects.value.length;
});

// Search and filter functions
const matchesSearch = (project) => {
    if (!searchQuery.value) return true;
    
    const query = searchQuery.value.toLowerCase();
    return (
        (project.name?.toLowerCase().includes(query)) ||
        (project.projectManager?.toLowerCase().includes(query)) ||
        (project.client?.toLowerCase().includes(query)) ||
        (project.id?.toString().includes(query))
    );
};

const matchesFilters = (project) => {
    if (activeFilters.value.length === 0) return true;
    
    // Check if the status filter is active
    if (activeFilters.value.includes('Confirmed') && project.status !== 'confirmed') {
        return false;
    }
    if (activeFilters.value.includes('Draft') && project.status !== 'draft') {
        return false;
    }
    
    return true;
};

const removeFilter = (filter) => {
    activeFilters.value = activeFilters.value.filter(f => f !== filter);
};

const clearAllFilters = () => {
    activeFilters.value = [];
};

onMounted(() => {
    // Generate projects with more realistic data
    projects.value = createProjects(20); // Generate fewer projects for testing
    console.log("Generated projects:", projects.value);
    
    // Make sure most projects are confirmed for testing
    projects.value.forEach(project => {
        // Set 80% of projects to confirmed status
        if (Math.random() < 0.8) {
            project.status = 'confirmed';
        }
        
        // Set some projects to start or end today
        const today = new Date();
        if (Math.random() < 0.5) {
            const projectDate = new Date(today);
            project.start = projectDate.toISOString().split('T')[0];
        }
        
        if (Math.random() < 0.5) {
            const projectDate = new Date(today);
            project.end = projectDate.toISOString().split('T')[0];
        }
    });
    
    // Add at least one guaranteed project for today's view
    projects.value.push({
        id: 999,
        projectNumber: 'WH-DEMO',
        name: 'Demo Project (Today)',
        client: 'Demo Client',
        start: new Date().toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0],
        time: '10:00',
        status: 'confirmed',
        projectManager: 'Warehouse Manager',
        type: 'Truck Unload',
        itemsCount: 85,
        totalItems: 85
    });
    
    confirmedProjects.value = projects.value.filter(project => project.status === 'confirmed');
    console.log("Confirmed projects:", confirmedProjects.value);

    // Filter and update counts
    filterProjects();
});

const filterProjects = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    
    console.log("Filtering projects for today:", today.toISOString().split('T')[0]);
    
    // Find projects starting today that are specifically incoming projects
    incomingProjects.value = confirmedProjects.value.filter(project => {
        try {
            // Convert project.start to Date object for comparison
            if (!project.start) return false;
            
            let projectDate;
            if (typeof project.start === 'string') {
                // Handle different date formats
                if (project.start.includes('/')) {
                    // Handle dd/mm/yyyy format
                    const [day, month, year] = project.start.split('/');
                    projectDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                } else {
                    // Handle yyyy-mm-dd format
                    projectDate = new Date(project.start);
                }
            } else {
                projectDate = new Date(project.start);
            }
            
            // Set to start of day for accurate comparison
            projectDate.setHours(0, 0, 0, 0);
            
            const isToday = projectDate.getDate() === today.getDate() && 
                          projectDate.getMonth() === today.getMonth() && 
                          projectDate.getFullYear() === today.getFullYear();
            
            // Check if this is an incoming type project
            const projectType = project.type || '';
            const isIncomingType = ['Customer Return', 'Truck Unload', 'Other In'].includes(projectType) || 
                               projectType.toLowerCase().includes('in') || 
                               projectType.toLowerCase().includes('return') || 
                               projectType.toLowerCase().includes('unload');
            
            return isToday && isIncomingType;
        } catch (e) {
            console.error("Error parsing date:", e, project.start);
            return false;
        }
    }).map(project => ({
        ...project,
        // Add additional fields that the TodayComponent expects
        type: project.type || 'Truck Unload', // Default to incoming type
        startTime: project.start,
        items: project.itemsCount || 50,
        client: project.client || 'Client Name',
        time: project.time || '09:00',
        jobNumber: project.projectNumber || `WH-${project.id}`
    }));
    
    console.log("Incoming projects for today:", incomingProjects.value);
    
    // If no projects were found, add a guaranteed demo incoming project
    if (incomingProjects.value.length === 0) {
        incomingProjects.value.push({
            id: 1001,
            projectNumber: 'WH-IN-DEMO',
            name: 'Demo Incoming Project',
            client: 'Demo Client',
            start: new Date().toISOString().split('T')[0],
            time: '11:30',
            status: 'confirmed',
            projectManager: 'Warehouse Manager',
            type: 'Truck Unload',
            itemsCount: 95,
            totalItems: 95,
            startTime: new Date(),
            items: 95,
            jobNumber: 'WH-IN-1001'
        });
    }
    
    // Find upcoming projects (next 7 days) that are outgoing projects
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    
    outboundProjects.value = projects.value.filter(project => {
        try {
            if (!project.start) return false;
            
            let projectDate;
            if (typeof project.start === 'string') {
                // Handle different date formats
                if (project.start.includes('/')) {
                    // Handle dd/mm/yyyy format
                    const [day, month, year] = project.start.split('/');
                    projectDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                } else {
                    // Handle yyyy-mm-dd format
                    projectDate = new Date(project.start);
                }
            } else {
                projectDate = new Date(project.start);
            }
            
            // Set to start of day for accurate comparison
            projectDate.setHours(0, 0, 0, 0);
            
            // Check if project date is after today and before or equal to nextWeek
            const isInDateRange = projectDate >= today && projectDate <= nextWeek;
            
            // Check if this is an outgoing type project
            const projectType = project.type || '';
            const isOutgoingType = ['Customer Collection', 'Truck Load', 'Other Out'].includes(projectType) || 
                               projectType.toLowerCase().includes('out') || 
                               projectType.toLowerCase().includes('load') ||
                               projectType.toLowerCase().includes('collection');
            
            return isInDateRange && isOutgoingType;
        } catch (e) {
            console.error("Error parsing date:", e, project.start);
            return false;
        }
    });
    
    // If no outgoing projects were found, add a guaranteed demo outgoing project
    if (outboundProjects.value.length === 0) {
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        
        outboundProjects.value.push({
            id: 2001,
            projectNumber: 'WH-OUT-DEMO',
            name: 'Demo Outbound Project',
            client: 'Demo Client',
            start: tomorrow.toISOString().split('T')[0],
            time: '14:00',
            status: 'confirmed',
            projectManager: 'Warehouse Manager',
            type: 'Truck Load',
            itemsCount: 120,
            totalItems: 120,
            totalPrepped: 80
        });
    }
    
    console.log("Outbound projects:", outboundProjects.value);
    
    incomingCount.value = incomingProjects.value.length;
    outboundCount.value = outboundProjects.value.length;
    
    console.log("Incoming Count:", incomingCount.value);
    console.log("Outbound Count:", outboundCount.value);
};
</script>

<style scoped>
:deep(.p-tabview-panels) {
    padding: 0;
}
</style>
